import React, { Component, FC, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { RawLoader } from '../loading/loading';
import { LocationsMapContainerProps, Props } from './mapContainerImpl';

const LazyLocationMapContainer = lazy(() => import('./mapContainerImpl'));
const LazyMyShipmentsMapContainer = lazy(() =>
	import('./mapContainerImpl').then(module => ({ default: module.MyShipmentsMapContainer }))
);
const LazyLocationsMapContainer = lazy(() =>
	import('./mapContainerImpl').then(module => ({ default: module.LocationsMapContainer }))
);

const LazyMapFallback: FC = () => (
	<div
		style={{
			alignItems: 'center',
			display: 'flex',
			height: '100%',
			justifyContent: 'center',
			minHeight: '12rem',
			width: '100%',
		}}>
		<RawLoader />
	</div>
);

const MapUnavailableFallback: FC = () => {
	const { t } = useTranslation('common');

	return (
		<div
			style={{
				alignItems: 'center',
				background: '#f5f5f5',
				color: '#4a4a4a',
				display: 'flex',
				fontSize: '2rem',
				height: '100%',
				justifyContent: 'center',
				minHeight: '12rem',
				padding: '1rem',
				textAlign: 'center',
				width: '100%',
			}}>
			{t('misc.map-unavailable')}
		</div>
	);
};

class MapErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
	state = { hasError: false };

	static getDerivedStateFromError(): { hasError: boolean } {
		return { hasError: true };
	}

	componentDidCatch(): void {}

	render(): React.ReactNode {
		if (this.state.hasError) return <MapUnavailableFallback />;

		return this.props.children;
	}
}

const withSuspense =
	<TProps extends object>(Component: React.ComponentType<TProps>): FC<TProps> =>
	props =>
		(
			<MapErrorBoundary>
				<Suspense fallback={<LazyMapFallback />}>
					<Component {...props} />
				</Suspense>
			</MapErrorBoundary>
		);

const LocationMapContainer = withSuspense<Props>(LazyLocationMapContainer);

export const MyShipmentsMapContainer = withSuspense<Props>(LazyMyShipmentsMapContainer);
export const LocationsMapContainer = withSuspense<LocationsMapContainerProps>(LazyLocationsMapContainer);

export default LocationMapContainer;
